import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { Button } from "@repo/blacksmith-ui/button";
import { Dialog } from "@repo/blacksmith-ui/dialog";
import { assetUrl } from "@/lib/assetUrl";
import { cn } from "@repo/blacksmith-ui/cn";
export const CODESMITH_INTRO_VIDEO_SEEN_STORAGE_KEY = "codesmith-intro-video-seen";

// Promo clip served from dashboard/public/assets (native 1200×680).
const INTRO_VIDEO_URL = assetUrl("codesmith-promo-in-app2.mp4");

/**
 * Warm the browser cache for the intro promo video from any dashboard page,
 * so the clip is already fetched by the time a first-time user opens the
 * Codesmith tab. Skipped once the seen key is set (the modal will never
 * show again) and deferred to idle time so it can't compete with page load.
 */
export function useCodesmithIntroVideoPrefetch() {
  const $ = _c(2);
  const didRun = useRef(false);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      if (didRun.current) {
        return;
      }
      didRun.current = true;
      try {
        if (window.localStorage.getItem(CODESMITH_INTRO_VIDEO_SEEN_STORAGE_KEY) === "true") {
          return;
        }
      } catch {}
      const prefetch = _temp;
      if (typeof window.requestIdleCallback === "function") {
        const idleId = window.requestIdleCallback(prefetch, {
          timeout: 5000
        });
        return () => window.cancelIdleCallback(idleId);
      }
      const timeoutId = window.setTimeout(prefetch, 2000);
      return () => window.clearTimeout(timeoutId);
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
}

/**
 * Whether the intro video modal should auto-open for this visit. The pool
 * gates mirror the onboarding modal's: only auto-pop for a user who has
 * never created a session, and never over a PR-link or task-first entry or
 * while a superuser is impersonating a foreign org.
 */
function _temp() {
  const link = document.createElement("link");
  link.rel = "prefetch";
  link.as = "video";
  link.href = INTRO_VIDEO_URL;
  document.head.appendChild(link);
}
export function shouldShowIntroVideoModal({
  hasSeenIntroVideo,
  userHasNoOwnSessions,
  hasLinkedPrFromUrl,
  skipIntroVideo,
  isImpersonating
}: {
  hasSeenIntroVideo: boolean;
  userHasNoOwnSessions: boolean;
  hasLinkedPrFromUrl: boolean;
  skipIntroVideo: boolean;
  isImpersonating: boolean;
}): boolean {
  return !hasSeenIntroVideo && userHasNoOwnSessions && !hasLinkedPrFromUrl && !skipIntroVideo && !isImpersonating;
}
function IntroVideo() {
  const $ = _c(3);
  const videoRef = useRef(null);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      const video = videoRef.current;
      if (!video) {
        return;
      }
      video.muted = true;
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(_temp2);
      }
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="relative w-full overflow-hidden bg-blacksmith-yellow" style={{
      aspectRatio: "1200 / 680"
    }}><video ref={videoRef} src={INTRO_VIDEO_URL} className="absolute inset-0 h-full w-full object-cover" autoPlay={true} loop={true} muted={true} playsInline={true} preload="auto" aria-label="[code]smith demo" /></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}

/**
 * First-visit intro modal that plays the Codesmith promo video. Uses a
 * viewport-wide blurred backdrop so everything behind it — including the
 * app sidebar rail — dims consistently.
 */
function _temp2() {}
export function CodesmithIntroVideoModal(t0) {
  const $ = _c(17);
  const {
    open,
    onOpenChange
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = cn("fixed left-[50%] top-[50%] z-50 flex w-[min(560px,calc(100%-2rem))] max-w-none translate-x-[-50%] translate-y-[-50%] flex-col gap-0 overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-app-background)] p-0 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95");
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <IntroVideo />;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <DialogPrimitive.Title className="type-body-m-medium m-0 text-neutral-900 dark:text-neutral-50">Meet [code]smith</DialogPrimitive.Title>;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <DialogPrimitive.Description className="type-body-s-regular m-0 text-neutral-600 dark:text-neutral-400">Agents shouldn't die when your laptop lid closes: [code]smith builds in the cloud, takes delegation from Slack, and keeps PRs green by fixing CI, answering reviewers, and resolving conflicts.<strong className="mt-2 block font-semibold text-neutral-900 dark:text-neutral-50">[code]smith is a paid product and is not covered under Blacksmith's free usage quota.</strong></DialogPrimitive.Description>;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== onOpenChange) {
    t6 = <Button onClick={() => onOpenChange(false)}>Get started</Button>;
    $[5] = onOpenChange;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Button asChild={true} variant="secondary"><a href="https://docs.blacksmith.sh/codesmith/overview" target="_blank" rel="noopener noreferrer">View docs</a></Button>;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== t6) {
    t8 = <div className="flex flex-col items-start gap-2 p-5 pb-6 text-left">{t4}{t5}<div className="mt-3 flex flex-wrap items-center gap-2">{t6}{t7}</div></div>;
    $[8] = t6;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <DialogPrimitive.Close className="absolute right-4 top-4 rounded-xs opacity-70 ring-offset-white transition-opacity hover:opacity-100 focus-visible:outline-none border border-transparent focus-visible:border-2 focus-visible:border-[var(--focus-border)] focus-visible:ring-0 disabled:pointer-events-none dark:ring-offset-neutral-950" aria-label="Close"><X className="h-4 w-4 text-white" /><span className="sr-only">Close</span></DialogPrimitive.Close>;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== t8) {
    t10 = <DialogPrimitive.Portal>{t1}<DialogPrimitive.Content className={t2} onOpenAutoFocus={_temp3} onInteractOutside={_temp4} onFocusOutside={_temp5}>{t3}{t8}{t9}</DialogPrimitive.Content></DialogPrimitive.Portal>;
    $[11] = t8;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== onOpenChange || $[14] !== open || $[15] !== t10) {
    t11 = <Dialog open={open} onOpenChange={onOpenChange}>{t10}</Dialog>;
    $[13] = onOpenChange;
    $[14] = open;
    $[15] = t10;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  return t11;
}
function _temp5(event_1) {
  return event_1.preventDefault();
}
function _temp4(event_0) {
  return event_0.preventDefault();
}
function _temp3(event) {
  return event.preventDefault();
}